import { useEffect, useMemo, useState } from 'react';
import { Box, Button, Tab, Tabs, useTheme } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import PageHeader from '../../../../components/PageHeader';
import { sharpsIcon } from '../../../../icons';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { getMedicalWaste } from '../../../../services/wasteManagementService';
import { MedicalWasteItem, WasteStatus } from '../types';
import constants from '../../../../constants';
import { ComplianceWrapper } from '../components/ComplianceWrapper';
import WasteItem from './MedicalWasteContainer';
import MedicalWasteContainersImage from '../../../../images/items/medical_waste_containers.jpg';
import { sendEmail } from '../../../../services/emailService';
import { selectCurrentUser } from '../../../../redux/user';
import {
  getSubscriptionData,
  selectAutoShipmentItems,
  selectLoadingState,
  selectSubscriptionCustomerNumber,
  selectSubscriptionItems,
  selectTimeBasedTbrItems
} from '../../../../redux/subscription';
import { addError, addSuccess } from '../../../../redux/alerts';
import { useNavigate } from 'react-router-dom';
import useCustomerNumber from '../../../../hooks/useCustomerNumber';
import useIsCorpView from '../../../../hooks/useIsCorpView';

const initialCompliance = {
  message_long: '',
  compliance_status: 4
};

const MedicalWaste = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const isCorpView = useIsCorpView()
  const currentUser = useAppSelector(selectCurrentUser);
  const tbrItems = useAppSelector(selectTimeBasedTbrItems);
  const subscriptionCustomerNumber = useAppSelector(selectSubscriptionCustomerNumber);
  const loadingState = useAppSelector(selectLoadingState);
  const subscriptionItems = useAppSelector(selectSubscriptionItems);
  const autoShipmentItems = useAppSelector(selectAutoShipmentItems);

  const [medicalWaste, setMedicalWaste] = useState<MedicalWasteItem[]>([]);
  const [medicalWasteCompliance, setMedicalWasteCompliance] = useState(initialCompliance);
  const [tabIndex, setTabIndex] = useState<number>(0);
  const [emailLoading, setEmailLoading] = useState<boolean>(false);

  const customerNumber = useCustomerNumber()

  const { hasEligibleAutoShipments, hasAutoShipments } = useMemo(() => {
    if (!medicalWaste.length || (!tbrItems.length && !subscriptionItems.length)) {
      return { hasEligibleAutoShipments: false, hasAutoShipments: false }
    }

    const medicalWasteIds = new Set(
      medicalWaste.map((mwaste) => mwaste.item_number.trim())
    )
    const subscriptionIds = subscriptionItems.map((item) => item.itemId.trim())
    const subscriptionMatches = subscriptionIds.filter((id) => medicalWasteIds.has(id))

    if (subscriptionMatches.length) {
      return { hasEligibleAutoShipments: true, hasAutoShipments: true }
    }

    if (tbrItems.length) {
      const tbrEligibleIds = new Set(
        tbrItems.map((item) => item.ItemNumber.trim())
      )
      const tbrItemMatches = Array.from(medicalWasteIds).filter((id) => tbrEligibleIds.has(id))

      if (tbrItemMatches.length) {
        return { hasEligibleAutoShipments: true, hasAutoShipments: false }
      }
    }

    return { hasEligibleAutoShipments: false, hasAutoShipments: false }
  }, [medicalWaste, tbrItems, subscriptionItems])

  const isRenewal = (mwaste: MedicalWasteItem) => {
    if (!autoShipmentItems) return false;

    return !!autoShipmentItems.find((autoShipment) => parseInt(autoShipment.itemId, 10) === parseInt(mwaste.item_number, 10));
  };

  const getMedicalWasteData = async () => {
    const medicalWasteResponse = await getMedicalWaste(customerNumber)

    const {
      mwaste_containers,
      practice_mwaste_compliance,
    } = medicalWasteResponse

    if (practice_mwaste_compliance?.compliance) {
      setMedicalWasteCompliance({
        message_long: practice_mwaste_compliance.compliance.message_long,
        compliance_status: practice_mwaste_compliance.compliance.compliance_status
      })
    }

    const mwasteContainers = mwaste_containers
      .map((mwaste: MedicalWasteItem) => ({ ...mwaste, isRenewal: isRenewal(mwaste) }))

    setMedicalWaste(mwasteContainers)
  }

  const sendReorderEmail = async () => {
    if (!currentUser) return;
    setEmailLoading(true);

    try {
      const emailDetails = {
        firstname: currentUser.firstname,
        lastname: currentUser.lastname,
        userEmail: currentUser.email,
        notificationName: 'sharpsContainerReorder'
      }
      await sendEmail(emailDetails, customerNumber);
      setEmailLoading(false);
      dispatch(addSuccess('Your request has been sent to one of our compliance managers. You will be contacted shortly.'));
    } catch (error) {
      console.error(error);
      dispatch(addError(error instanceof Error ? error.message : 'There was a problem sending your request, please try again later'));
      setEmailLoading(false);
    }
  };

  const getInUseItems: MedicalWasteItem[] = useMemo(() => {
    return medicalWaste
      .filter(mwaste => mwaste.disposal_status === WasteStatus.inUse)
      .sort((a, b) => (a.shipped_date < b.shipped_date ? -1 : a.shipped_date > b.shipped_date ? 1 : 0));
  }, [medicalWaste]);

  const getUsedItems: MedicalWasteItem[] = useMemo(() => {
    return medicalWaste
      .filter(mwaste => mwaste.disposal_status === WasteStatus.used)
      .sort((a, b) => {
        if (!a.used_date && !b.used_date) return 0;
        if (!a.used_date) return 1;
        if (!b.used_date) return -1;

        return a.used_date > b.used_date ? -1 : a.used_date < b.used_date ? 1 : 0;
      });
  }, [medicalWaste]);

  const getProcessedItems: MedicalWasteItem[] = useMemo(() => {
    return medicalWaste
      .filter(mwaste => mwaste.disposal_status === WasteStatus.destroyed)
      .sort((a, b) => (a.destroyed_date > b.destroyed_date ? -1 : a.destroyed_date < b.destroyed_date ? 1 : 0));
  }, [medicalWaste]);

  useEffect(() => {
    if (customerNumber) {
      getMedicalWasteData();

      if (loadingState === 'not-asked' || subscriptionCustomerNumber !== customerNumber) {
        dispatch(getSubscriptionData(customerNumber));
      }
    }
  }, [customerNumber]);

  const pageTabs = [`In Use(${getInUseItems.length})`, `Used(${getUsedItems.length})`, `Processed(${getProcessedItems.length})`];

  const AutoShipmentButton = () => {
    if (hasEligibleAutoShipments) {
      const link = isCorpView
        ? `/corp/practice/${customerNumber}/auto-renewals`
        : `/practice/auto-renewals`;
      return (
        <div className="flex flex-end m-b-16 m-r-16">
          {hasAutoShipments ? (
            <Button variant="contained" component="button" onClick={() => navigate(link)}>
              <EditIcon fontSize="small" /> Edit Auto Shipments
            </Button>
          ) : (
            <Button variant="contained" component="button" onClick={() => navigate(link)}>
              <AddIcon fontSize="small" /> Enroll in Auto Shipments
            </Button>
          )}
        </div>
      );
    }

    return;
  };

  const TabContent = () => {
    const containers = [ getInUseItems, getUsedItems, getProcessedItems ]
    const messages = [ null, 'There are no used items', 'There are no processed items' ]

    const wasteContainers = containers[tabIndex] 
    const hasContainers = wasteContainers.length > 0    

    return (
      <Box sx={{ p: '0 16px 16px 16px' }}>
        {
          tabIndex === 0 && (
            <Box className="flex flex-center flex-align-center m-b-16">
              <img style={{ width: '125px' }} className="m-r-16" src={MedicalWasteContainersImage} alt="Medical Waste Containers" />
              <Box className="flex flex-column">
                {
                  wasteContainers.length === 0 && (
                    <Box className="f-s-23 m-b-8">
                      Your office is currently out of Sharps containers. Reorder now.
                    </Box>
                  )
                }
                {
                  wasteContainers.length === 1 && (
                    <Box className="f-s-23 m-b-8">
                      Only 1 Sharps container is left in your office. Reorder now.
                    </Box>
                  )
                }
                <Box className="center">
                  <Button onClick={sendReorderEmail} variant="contained" disabled={emailLoading}>
                    Reorder Sharps Containers
                  </Button>
                </Box>
              </Box>
            </Box>
          )
        }
        {
          hasContainers && (
            wasteContainers.map(mwaste => (
              <WasteItem
                key={mwaste.mwaste_id}
                mwaste={mwaste}
                getWasteData={getMedicalWasteData}
              />
            ))
          )
        }
        {
          !hasContainers && (
            <Box>
              { messages[tabIndex] }
            </Box>
          )
        }
      </Box>
    )
  };

  return (
    <>
      <PageHeader src={sharpsIcon} headerName={'Sharps & Medical Waste'} size={'26px'} />
      {medicalWaste.length === 0 ? (
        <iframe className="iframe-sales-page" data-testid="sales-iframe" src={constants.MEDICAL_WASTE_SALES_PAGE_URL} />
      ) : (
        <>
          <ComplianceWrapper compliance={medicalWasteCompliance} />
          <Box sx={{ color: theme.palette.color50555b.main }}>
            <Box>
              <AutoShipmentButton />
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }} className="flex flex-end m-b-16">
                <Tabs value={tabIndex} onChange={(e, newValue) => setTabIndex(newValue)}>
                  {pageTabs.length && pageTabs.map((tab, index) => <Tab key={index} label={tab} />)}
                </Tabs>
              </Box>
              <TabContent />
            </Box>
          </Box>
        </>
      )}
    </>
  );
};

export default MedicalWaste;
